import { ReportType } from '@customTypes/order';
import { OrderType } from '@pages/results/Results.types';

import {
  APOE_PATIENT_UNDERSTANDING,
  BASELINE_CANCER_SAMPLE,
  BASELINE_CARDIOMETABOLIC_SAMPLE,
  BREAST_CANCER_IPRS_SAMPLES,
  CAD_SAMPLES,
  PGX_SAMPLES,
  PROSTATE_CANCER_IPRS_SAMPLES,
  SINGLE_GENE_SAMPLES,
  TYPE_2_DIABETES_IPRS_SAMPLES,
} from './RequestDetails.consts';

export interface ResourceLink {
  title: string;
  url: string;
}

export interface ResourceSet {
  title: string;
  samples: ResourceLink[];
}

const SINGLE_GENE_LEARN_MORE_TITLE = 'Learn more about Single-Gene Risk';
const BASELINE_LEARN_MORE_TITLE = 'Learn more about Baseline Risk';
const CAD_IPRS_LEARN_MORE_TITLE = 'Learn more about CAD iPRS';
const TYPE_2_DIABETES_IPRS_LEARN_MORE_TITLE = 'Learn more about Type 2 Diabetes iPRS';
const BREAST_CANCER_IPRS_LEARN_MORE_TITLE = 'Learn more about Breast Cancer iPRS';
const PROSTATE_CANCER_IPRS_LEARN_MORE_TITLE = 'Learn more about Prostate Cancer iPRS';
const MEDICATION_RESPONSE_LEARN_MORE_TITLE = 'Learn more about Medication Response';
const APOE_LEARN_MORE_TITLE = 'Learn more about APOE';

const isPlusProduct = (productName: string): boolean =>
  productName.includes('Proactive Health Plus') || /^Atlas(?: Cancer| Cardiometabolic)? Plus(?:,|$)/.test(productName);

const isSelectProduct = (productName: string): boolean =>
  (productName.includes('Proactive Health') && !productName.includes('Plus')) ||
  /^Atlas(?: Cancer| Cardiometabolic)? Select(?:,|$)/.test(productName);

const isIntegratedRiskProduct = (productName: string): boolean =>
  /integrated/i.test(productName) || /iPRS/i.test(productName);

/**
 * Resolves the Single-Gene Risk technical one-pager based on the gene-panel size in the product name.
 * The 84/81/151 panels each have their own one-pager; we always include the product one-pager
 * and the patient-facing "Understanding Your Test Results" PDF.
 */
function buildSingleGeneSamples(productName: string): ResourceLink[] {
  const samples: ResourceLink[] = [SINGLE_GENE_SAMPLES[0], SINGLE_GENE_SAMPLES[1]];
  if (productName.includes('84 Genes')) {
    samples.push(SINGLE_GENE_SAMPLES[3]);
  } else if (productName.includes('81 Genes')) {
    samples.push(SINGLE_GENE_SAMPLES[4]);
  } else if (productName.includes('151 Genes')) {
    samples.push(SINGLE_GENE_SAMPLES[2]);
  }
  return samples;
}

// Product one-pager + understanding results, then Plus vs non-Plus technical one-pager.
function buildCatalogSamples(samples: ResourceLink[], productName: string): ResourceLink[] {
  const resolved: ResourceLink[] = [samples[0], samples[1]];
  if (isPlusProduct(productName)) {
    resolved.push(samples[3]);
  } else if (isSelectProduct(productName)) {
    resolved.push(samples[2]);
  }
  return resolved;
}

function buildPgxSamples(productName: string): ResourceLink[] {
  return buildCatalogSamples(PGX_SAMPLES, productName);
}

function buildApoeSamples(): ResourceLink[] {
  return [APOE_PATIENT_UNDERSTANDING];
}

function getIntegratedRiskResources(productName: string): ResourceSet {
  if (isIntegratedRiskProduct(productName) && /type 2 diabetes/i.test(productName)) {
    return {
      title: TYPE_2_DIABETES_IPRS_LEARN_MORE_TITLE,
      samples: buildCatalogSamples(TYPE_2_DIABETES_IPRS_SAMPLES, productName),
    };
  }
  if (isIntegratedRiskProduct(productName) && /breast cancer/i.test(productName)) {
    return {
      title: BREAST_CANCER_IPRS_LEARN_MORE_TITLE,
      samples: buildCatalogSamples(BREAST_CANCER_IPRS_SAMPLES, productName),
    };
  }
  if (isIntegratedRiskProduct(productName) && /prostate cancer/i.test(productName)) {
    return {
      title: PROSTATE_CANCER_IPRS_LEARN_MORE_TITLE,
      samples: buildCatalogSamples(PROSTATE_CANCER_IPRS_SAMPLES, productName),
    };
  }
  return {
    title: CAD_IPRS_LEARN_MORE_TITLE,
    samples: buildCatalogSamples(CAD_SAMPLES, productName),
  };
}

function getBaselineSamples(productName: string): ResourceLink[] {
  const isCancer = /breast|prostate|atlas cancer/i.test(productName);
  const isCardiometabolic = /type 2 diabetes|coronary|cardiometabolic|\bCAD\b/i.test(productName);
  if (isCancer && !isCardiometabolic) return [BASELINE_CANCER_SAMPLE];
  if (isCardiometabolic && !isCancer) return [BASELINE_CARDIOMETABOLIC_SAMPLE];
  return [BASELINE_CARDIOMETABOLIC_SAMPLE, BASELINE_CANCER_SAMPLE];
}

function getRiskScoreResources(productName: string, reportType: ReportType | undefined): ResourceSet {
  if (reportType === ReportType.BASELINE_RISK) {
    return {
      title: BASELINE_LEARN_MORE_TITLE,
      samples: getBaselineSamples(productName),
    };
  }
  return getIntegratedRiskResources(productName);
}

export function getNextStepsResources(
  orderType: OrderType,
  productName: string,
  reportType: ReportType | undefined,
): ResourceSet {
  switch (orderType) {
    case OrderType.SINGLE_GENE:
      return {
        title: SINGLE_GENE_LEARN_MORE_TITLE,
        samples: buildSingleGeneSamples(productName),
      };
    case OrderType.RISK_SCORE:
      return getRiskScoreResources(productName, reportType);
    case OrderType.APOE:
      return {
        title: APOE_LEARN_MORE_TITLE,
        samples: buildApoeSamples(),
      };
    default:
      return {
        title: MEDICATION_RESPONSE_LEARN_MORE_TITLE,
        samples: buildPgxSamples(productName),
      };
  }
}
